import { Box, ListItemText, MenuItem, Select, type SelectChangeEvent, Typography } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import type { AppState } from '@store/store';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

interface EmployeeSelectProps {
    value?: number;
    disabled?: boolean;
    employees?: Array<{ id: number; title: string; pictureId?: string; email?: string }>;
    onChange: (val: number | undefined) => void;
}

/**
 * EmployeeSelect component.
 *
 * @param {Object} props - The props for the EmployeeSelect component.
 * @param {EmployeeSelectProps} props - The props for the EmployeeSelect component.
 * @returns {JSX.Element} - The rendered EmployeeSelect component.
 */
function EmployeeSelect({ value, onChange, disabled, employees }: EmployeeSelectProps) {
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employees;
    const [open, setOpen] = useState(false);

    const employeesToUse = employees || allEmployees;

    const handleChange = (event: SelectChangeEvent<number>) => {
        const value = event.target.value;
        const selectedValue = typeof value === 'string' ? parseInt(value) : value;
        onChange(selectedValue || undefined);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const sortedEmployees = [...employeesToUse].sort((a, b) => {
        return a.title.localeCompare(b.title);
    });

    // Finde den ausgewählten Mitarbeiter für die Anzeige im Select
    const selectedEmployee = value ? sortedEmployees.find((e) => e.id === value) : undefined;

    // Extrahiere Vor- und Nachname aus dem Titel
    const getNameParts = (title: string) => {
        const parts = title.split(' ');
        // Wenn der Name mehr als zwei Teile hat, nehmen wir an, dass der letzte Teil der Nachname ist
        // und alles davor der Vorname
        if (parts.length > 2) {
            return {
                firstName: parts.slice(0, -1).join(' '),
                lastName: parts[parts.length - 1],
            };
        }
        // Sonst nehmen wir den ersten Teil als Vorname und den Rest als Nachname
        return {
            firstName: parts[0] || '',
            lastName: parts.slice(1).join(' ') || '',
        };
    };

    const renderEmployeePicture = (employee: { id: number; title: string; pictureId?: string | null }) => {
        const { firstName, lastName } = getNameParts(employee.title);

        return (
            <EmployeePicture
                employee={{
                    id: employee.id,
                    firstName: firstName,
                    lastName: lastName,
                    pictureId: employee.pictureId || null,
                }}
                avatarSize={40}
            />
        );
    };

    return (
        <Select
            size="small"
            displayEmpty
            id="employee-select"
            value={value || ''}
            onChange={handleChange}
            onClose={handleClose}
            onOpen={handleOpen}
            open={open}
            disabled={disabled}
            MenuProps={{
                PaperProps: {
                    style: {
                        maxHeight: 300,
                    },
                },
            }}
            sx={{
                minWidth: 200,
                '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 14px',
                    fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                },
            }}
            renderValue={(selected) => {
                if (!selected) {
                    return (
                        <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            {langStrings.oftenUsed.pleaseSelect}
                        </Typography>
                    );
                }

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {selectedEmployee && (
                            <>
                                {renderEmployeePicture(selectedEmployee)}
                                <Typography>{selectedEmployee.title}</Typography>
                            </>
                        )}
                    </Box>
                );
            }}
        >
            {sortedEmployees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id} sx={{ padding: '8px 16px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ marginRight: 2 }}>{renderEmployeePicture(employee)}</Box>
                        <ListItemText
                            primary={
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: selectedEmployee?.id === employee.id ? 600 : 400 }}
                                >
                                    {employee.title}
                                </Typography>
                            }
                            secondary={
                                employee.email && (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {employee.email}
                                    </Typography>
                                )
                            }
                        />
                    </Box>
                </MenuItem>
            ))}
        </Select>
    );
}

export default EmployeeSelect;
